import React, { useState, useCallback, useEffect } from 'react';
import { Cloud, Trash2, RefreshCw, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const SYNC_KEYS = [
  'gym_notebook_walking_session_history',
  'gym_notebook_walking_daily_data',
  'gym_notebook_walking_routines',
  'gym_notebook_walking_stride_length',
  'gym_notebook_meal_times',
  'gym_notebook_nutrition_goals',
  'gym_notebook_waist_history',
  'gym_notebook_body_measurements_history',
  'gym_notebook_persistent_bodyweight',
  'gym_notebook_persistent_weight_unit',
  'gym_notebook_persistent_height',
  'gym_notebook_persistent_height_unit',
  'gym_notebook_persistent_age',
  'gym_notebook_persistent_gender',
  'gym_notebook_persistent_measurements',
  'gym_notebook_waist',
  'gym_notebook_waist_unit',
  'gym_notebook_weight_goal',
  'gym_notebook_weight_goal_start',
  'medications_enabled',
];

function collectAll() {
  const data = {};
  SYNC_KEYS.forEach(k => {
    const v = localStorage.getItem(k);
    if (v !== null) data[k] = v;
  });
  const nutrition = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('nutrition_') && key.length <= 25) {
      nutrition[key] = localStorage.getItem(key);
    }
  }
  return { data, nutrition };
}

function DataBackupContent() {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [counts, setCounts] = useState({ keys: 0, nutritionDays: 0 });

  const refreshCounts = useCallback(() => {
    const { data, nutrition } = collectAll();
    setCounts({ keys: Object.keys(data).length, nutritionDays: Object.keys(nutrition).length });
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data, nutrition } = collectAll();
      await api.post('/sync/cloud', { data, nutrition });
      setLastSync(new Date().toLocaleTimeString());
      toast.success('Data synced to cloud');
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm('Clear ALL local data? This removes nutrition, walking, and body data from this device. Cloud data is not affected.')) return;
    SYNC_KEYS.forEach(k => localStorage.removeItem(k));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('nutrition_')) localStorage.removeItem(key);
    }
    refreshCounts();
    toast.success('Local data cleared');
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-700/50 border-slate-600">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Cloud className="h-5 w-5 text-cyan-400" />
            Cloud Sync
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-3">
            Your data syncs automatically across devices when you log in.
          </p>
          <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
            <span>{counts.keys} settings</span>
            <span>{counts.nutritionDays} nutrition days</span>
            {lastSync && <span>Synced at {lastSync}</span>}
          </div>
          <Button
            onClick={handleSyncNow}
            disabled={syncing}
            className="bg-cyan-600 hover:bg-cyan-700 text-white w-full"
            data-testid="sync-now-btn"
          >
            {syncing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-700/50 border-slate-600">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Trash2 className="h-5 w-5 text-red-400" />
            Clear Local Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-3">
            Removes data from this device only. Cloud data stays safe.
          </p>
          <Button onClick={handleClear} variant="outline" className="w-full border-red-500 text-red-400 hover:bg-red-500/20" data-testid="clear-local-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Clear Local Data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export const DataBackupDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Download className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-white">Data Backup</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <Download className="h-4 w-4 text-white" />
            </div>
            Data Backup
          </DialogTitle>
        </DialogHeader>
        <DataBackupContent />
      </DialogContent>
    </Dialog>
  );
};

export default DataBackupDialog;
